import "@/App.css";
import { lazy, Suspense, useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";

// Wrap lazy imports so a stale code-split chunk (after a new deploy) auto-recovers
// with a one-time full reload instead of leaving a blank white screen.
function lazyWithReload(factory) {
  const wrapped = () =>
    factory().catch((err) => {
      const KEY = "chunk_reload_at";
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return new Promise(() => {}); // hold render while the page reloads
      }
      throw err;
    });
  return lazy(wrapped);
}

const Home = lazyWithReload(() => import("@/pages/Home"));
const Tipsters = lazyWithReload(() => import("@/pages/Tipsters"));
const TipsterProfile = lazyWithReload(() => import("@/pages/TipsterProfile"));
const CategoryLanding = lazyWithReload(() => import("@/pages/CategoryLanding"));
const PlatformLanding = lazyWithReload(() => import("@/pages/PlatformLanding"));
const EventHub = lazyWithReload(() => import("@/pages/EventHub"));
const EventsIndex = lazyWithReload(() => import("@/pages/EventsIndex"));
const ToolsIndex = lazyWithReload(() => import("@/pages/ToolsIndex"));
const BreakEvenCalculator = lazyWithReload(() => import("@/pages/BreakEvenCalculator"));
const VarianceSimulator = lazyWithReload(() => import("@/pages/VarianceSimulator"));
const TipsterAlternatives = lazyWithReload(() => import("@/pages/TipsterAlternatives"));
const Guides = lazyWithReload(() => import("@/pages/Guides"));
const GuideDetail = lazyWithReload(() => import("@/pages/GuideDetail"));
const BettingTools = lazyWithReload(() => import("@/pages/BettingTools"));
const HubIndex = lazyWithReload(() => import("@/pages/HubIndex"));
const Blog = lazyWithReload(() => import("@/pages/Blog"));
const BlogPost = lazyWithReload(() => import("@/pages/BlogPost"));
const Login = lazyWithReload(() => import("@/pages/Login"));
const Register = lazyWithReload(() => import("@/pages/Register"));
const VerifyEmail = lazyWithReload(() => import("@/pages/VerifyEmail"));
const Contact = lazyWithReload(() => import("@/pages/Contact"));
const LandingPage = lazyWithReload(() => import("@/pages/LandingPage"));
const BookmakerReviews = lazyWithReload(() => import("@/pages/BookmakerReviews"));
const BetTypes = lazyWithReload(() => import("@/pages/BetTypes"));
const BetTypeArticle = lazyWithReload(() => import("@/pages/BetTypeArticle"));
const BettingTerms = lazyWithReload(() => import("@/pages/BettingTerms"));
const BettingStrategies = lazyWithReload(() => import("@/pages/BettingStrategies"));
const StrategyArticle = lazyWithReload(() => import("@/pages/StrategyArticle"));
const MetricsHub = lazyWithReload(() => import("@/pages/MetricsHub"));
const SubmitTipster = lazyWithReload(() => import("@/pages/SubmitTipster"));
const MyReviews = lazyWithReload(() => import("@/pages/MyReviews"));
const AdminDashboard = lazyWithReload(() => import("@/pages/AdminDashboard"));
const AuthCallback = lazyWithReload(() => import("@/pages/AuthCallback"));
const Compare = lazyWithReload(() => import("@/pages/Compare"));
const TipsterVersus = lazyWithReload(() => import("@/pages/TipsterVersus"));
const CompareCategory = lazyWithReload(() => import("@/pages/CompareCategory"));
const About = lazyWithReload(() => import("@/pages/About"));
const HowWeScore = lazyWithReload(() => import("@/pages/HowWeScore"));
const ScamChecklist = lazyWithReload(() => import("@/pages/ScamChecklist"));
const ComparisonCheatSheet = lazyWithReload(() => import("@/pages/ComparisonCheatSheet"));
const ReviewPolicy = lazyWithReload(() => import("@/pages/ReviewPolicy"));
const HowWeMakeMoney = lazyWithReload(() => import("@/pages/HowWeMakeMoney"));
const EditorialPolicy = lazyWithReload(() => import("@/pages/EditorialPolicy"));
const Corrections = lazyWithReload(() => import("@/pages/Corrections"));
const TopicHub = lazyWithReload(() => import("@/pages/TopicHub"));
const TopicsIndex = lazyWithReload(() => import("@/pages/TopicsIndex"));
const ToolPage = lazyWithReload(() => import("@/pages/ToolPage"));
const GlossaryTerm = lazyWithReload(() => import("@/pages/GlossaryTerm"));
const MarketRanking = lazyWithReload(() => import("@/pages/MarketRanking"));
const MarketsIndex = lazyWithReload(() => import("@/pages/MarketsIndex"));
const Offers = lazyWithReload(() => import("@/pages/Offers"));
const ResponsibleGambling = lazyWithReload(() => import("@/pages/ResponsibleGambling"));
const Privacy = lazyWithReload(() => import("@/pages/Privacy"));
const Terms = lazyWithReload(() => import("@/pages/Terms"));
const Unsubscribe = lazyWithReload(() => import("@/pages/Unsubscribe"));
const MyShortlist = lazyWithReload(() => import("@/pages/MyShortlist"));
const ReviewerProfile = lazyWithReload(() => import("@/pages/ReviewerProfile"));
const TopReviewers = lazyWithReload(() => import("@/pages/TopReviewers"));
import { SCAM_HUB, EVALUATE_HUB, BASICS_HUB, SAFETY_HUB, PLATFORMS_HUB, CHOOSING_HUB, PSYCHOLOGY_HUB, BEATBOOKIE_HUB, SPORTSTRATEGY_HUB, BANKROLL_HUB, REVIEWS_HUB, EXCHANGES_HUB } from "@/lib/hubs";
function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" role="status" aria-label="Loading">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
    </div>
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    const msg = String((error && error.message) || error || "");
    if (/ChunkLoadError|Loading chunk|dynamically imported module|module script failed|Failed to fetch/i.test(msg)) {
      const KEY = "chunk_reload_at";
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center" data-testid="app-error-fallback">
          <h1 className="font-heading text-2xl font-bold">Something went wrong</h1>
          <p className="max-w-md text-muted-foreground">Please reload the page to continue.</p>
          <button onClick={() => window.location.reload()} className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground" data-testid="error-reload-button">
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function NotFound() {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center gap-4 px-4 text-center" data-testid="not-found-page">
      <h1 className="font-heading text-4xl font-bold">Page not found</h1>
      <p className="text-muted-foreground">The page you're looking for doesn't exist or has moved.</p>
      <a href="/" className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground" data-testid="not-found-home-link">Back to home</a>
    </div>
  );
}

function Shell({ children }) {
  const { pathname } = useLocation();
  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      {!/^\/tipster\/[^/]+\/?$/.test(pathname) && <NewsletterSignup />}
      <Footer />
      <ExitIntentPopup />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Scroll to top on every route (pathname) change — but not for in-page anchors.
  useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.hash]);
  // Emergent OAuth returns to root with #session_id=... — handle before anything else.
  if (location.hash?.includes("session_id=")) {
    return <Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>;
  }
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/" element={<Shell><Home /></Shell>} />
      <Route path="/tipsters" element={<Shell><Tipsters /></Shell>} />
      <Route path="/tipster/:slug" element={<Shell><TipsterProfile /></Shell>} />
      <Route path="/tipster/:slug/alternatives" element={<Shell><TipsterAlternatives /></Shell>} />
      <Route path="/best/:category" element={<Shell><CategoryLanding /></Shell>} />
      <Route path="/best/:category/:sub" element={<Shell><CategoryLanding /></Shell>} />
      <Route path="/guides" element={<Shell><Guides /></Shell>} />
      <Route path="/guides/:slug" element={<Shell><GuideDetail /></Shell>} />
      <Route path="/scam-hub" element={<Shell><HubIndex hub={SCAM_HUB} /></Shell>} />
      <Route path="/tipster-scam-checklist" element={<Shell><ScamChecklist /></Shell>} />
      <Route path="/tipster-comparison-cheat-sheet" element={<Shell><ComparisonCheatSheet /></Shell>} />
      <Route path="/scam-hub/:slug" element={<Shell><GuideDetail hub={SCAM_HUB} /></Shell>} />
      <Route path="/how-to-evaluate-a-tipster" element={<Shell><HubIndex hub={EVALUATE_HUB} /></Shell>} />
      <Route path="/how-to-evaluate-a-tipster/:slug" element={<Shell><GuideDetail hub={EVALUATE_HUB} /></Shell>} />
      <Route path="/betting-basics" element={<Shell><HubIndex hub={BASICS_HUB} /></Shell>} />
      <Route path="/betting-basics/:slug" element={<Shell><GuideDetail hub={BASICS_HUB} /></Shell>} />
      <Route path="/betting-safety" element={<Shell><HubIndex hub={SAFETY_HUB} /></Shell>} />
      <Route path="/betting-safety/:slug" element={<Shell><GuideDetail hub={SAFETY_HUB} /></Shell>} />
      <Route path="/tipster-platforms" element={<Shell><HubIndex hub={PLATFORMS_HUB} /></Shell>} />
      <Route path="/tipster-platforms/:slug" element={<Shell><GuideDetail hub={PLATFORMS_HUB} /></Shell>} />
      <Route path="/choosing-a-tipster" element={<Shell><HubIndex hub={CHOOSING_HUB} /></Shell>} />
      <Route path="/choosing-a-tipster/:slug" element={<Shell><GuideDetail hub={CHOOSING_HUB} /></Shell>} />
      <Route path="/betting-psychology" element={<Shell><HubIndex hub={PSYCHOLOGY_HUB} /></Shell>} />
      <Route path="/betting-psychology/:slug" element={<Shell><GuideDetail hub={PSYCHOLOGY_HUB} /></Shell>} />
      <Route path="/beat-the-bookmaker" element={<Shell><HubIndex hub={BEATBOOKIE_HUB} /></Shell>} />
      <Route path="/beat-the-bookmaker/:slug" element={<Shell><GuideDetail hub={BEATBOOKIE_HUB} /></Shell>} />
      <Route path="/sport-betting-strategy" element={<Shell><HubIndex hub={SPORTSTRATEGY_HUB} /></Shell>} />
      <Route path="/sport-betting-strategy/:slug" element={<Shell><GuideDetail hub={SPORTSTRATEGY_HUB} /></Shell>} />
      <Route path="/bankroll-and-staking" element={<Shell><HubIndex hub={BANKROLL_HUB} /></Shell>} />
      <Route path="/bankroll-and-staking/:slug" element={<Shell><GuideDetail hub={BANKROLL_HUB} /></Shell>} />
      <Route path="/tipster-reviews" element={<Shell><HubIndex hub={REVIEWS_HUB} /></Shell>} />
      <Route path="/tipster-reviews/:slug" element={<Shell><GuideDetail hub={REVIEWS_HUB} /></Shell>} />
      <Route path="/betting-tools" element={<Shell><BettingTools /></Shell>} />
      <Route path="/blog" element={<Shell><Blog /></Shell>} />
      <Route path="/blog/:slug" element={<Shell><BlogPost /></Shell>} />
      <Route path="/submit-tipster" element={<Shell><SubmitTipster /></Shell>} />
      <Route path="/my-reviews" element={<Shell><MyReviews /></Shell>} />
      <Route path="/admin" element={<Shell><AdminDashboard /></Shell>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<Shell><VerifyEmail /></Shell>} />
      <Route path="/contact" element={<Shell><Contact /></Shell>} />
      <Route path="/football-tipsters" element={<Shell><LandingPage slug="football-tipsters" /></Shell>} />
      <Route path="/horse-racing-tipsters" element={<Shell><LandingPage slug="horse-racing-tipsters" /></Shell>} />
      <Route path="/tennis-tipsters" element={<Shell><LandingPage slug="tennis-tipsters" /></Shell>} />
      <Route path="/golf-tipsters" element={<Shell><LandingPage slug="golf-tipsters" /></Shell>} />
      <Route path="/best-tipsters" element={<Shell><LandingPage slug="best-tipsters" /></Shell>} />
      <Route path="/tools" element={<Shell><ToolsIndex /></Shell>} />
      <Route path="/tools/break-even-calculator" element={<Shell><BreakEvenCalculator /></Shell>} />
      <Route path="/tools/variance-simulator" element={<Shell><VarianceSimulator /></Shell>} />
      <Route path="/tools/:slug" element={<Shell><ToolPage /></Shell>} />
      <Route path="/betting-terms/:slug" element={<Shell><GlossaryTerm /></Shell>} />
      <Route path="/markets" element={<Shell><MarketsIndex /></Shell>} />
      <Route path="/offers" element={<Shell><Offers /></Shell>} />
      <Route path="/markets/:slug" element={<Shell><MarketRanking /></Shell>} />
      <Route path="/betting-exchanges" element={<Shell><HubIndex hub={EXCHANGES_HUB} /></Shell>} />
      <Route path="/betting-exchanges/:slug" element={<Shell><GuideDetail hub={EXCHANGES_HUB} /></Shell>} />
      <Route path="/events" element={<Shell><EventsIndex /></Shell>} />
      <Route path="/events/:slug" element={<Shell><EventHub /></Shell>} />
      <Route path="/best-tipsters/:platform" element={<Shell><PlatformLanding /></Shell>} />
      <Route path="/best-tipsters/:platform/:sport" element={<Shell><PlatformLanding /></Shell>} />
      <Route path="/verified-tipsters" element={<Shell><LandingPage slug="verified-tipsters" /></Shell>} />
      <Route path="/free-tipsters" element={<Shell><LandingPage slug="free-tipsters" /></Shell>} />
      <Route path="/premium-tipsters" element={<Shell><LandingPage slug="premium-tipsters" /></Shell>} />
      <Route path="/bookmaker-reviews" element={<Shell><BookmakerReviews /></Shell>} />
      <Route path="/bet-types" element={<Shell><BetTypes /></Shell>} />
      <Route path="/bet-types/:slug" element={<Shell><BetTypeArticle /></Shell>} />
      <Route path="/betting-terms" element={<Shell><BettingTerms /></Shell>} />
      <Route path="/betting-strategies" element={<Shell><BettingStrategies /></Shell>} />
      <Route path="/betting-strategies/:slug" element={<Shell><StrategyArticle /></Shell>} />
      <Route path="/betting-metrics" element={<Shell><MetricsHub /></Shell>} />
      <Route path="/compare" element={<Shell><Compare /></Shell>} />
      <Route path="/compare/:a/vs/:b" element={<Shell><TipsterVersus /></Shell>} />
      <Route path="/compare/:slug" element={<Shell><CompareCategory /></Shell>} />
      <Route path="/about" element={<Shell><About /></Shell>} />
      <Route path="/how-we-score" element={<Shell><HowWeScore /></Shell>} />
      <Route path="/review-policy" element={<Shell><ReviewPolicy /></Shell>} />
      <Route path="/how-we-make-money" element={<Shell><HowWeMakeMoney /></Shell>} />
      <Route path="/editorial-policy" element={<Shell><EditorialPolicy /></Shell>} />
      <Route path="/corrections" element={<Shell><Corrections /></Shell>} />
      <Route path="/topics" element={<Shell><TopicsIndex /></Shell>} />
      <Route path="/topics/:slug" element={<Shell><TopicHub /></Shell>} />
      <Route path="/responsible-gambling" element={<Shell><ResponsibleGambling /></Shell>} />
      <Route path="/privacy" element={<Shell><Privacy /></Shell>} />
      <Route path="/terms" element={<Shell><Terms /></Shell>} />
      <Route path="/unsubscribe" element={<Shell><Unsubscribe /></Shell>} />
      <Route path="/my-shortlist" element={<Shell><MyShortlist /></Shell>} />
      <Route path="/reviewer/:userId" element={<Shell><ReviewerProfile /></Shell>} />
      <Route path="/community/top-reviewers" element={<Shell><TopReviewers /></Shell>} />
      <Route path="*" element={<Shell><NotFound /></Shell>} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster richColors position="top-center" />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
